import React from 'react';
import { useAppStore } from '../../../GlobalStore/GlobalStore';
import {requestProjectFiles} from "../../../Utils/jsBridge/sender/ide/requestProjectFiles.ts";

interface EmbeddingProgressProps {
    className?: string;
    isPreview: boolean;
    onDismiss: () => void;
}

const ProgressCircle: React.FC<{ progress: number }> = ({ progress }) => {
    const radius = 12;
    const strokeWidth = 2.5;
    const normalizedRadius = radius - strokeWidth * 2;
    const circumference = normalizedRadius * 2 * Math.PI;
    const strokeDashoffset = circumference - (progress / 100) * circumference;

    return (
        <div className="relative w-6 h-6">
            <svg
                className="w-6 h-6 transform -rotate-90"
                width={radius * 2}
                height={radius * 2}
            >
                {/* Background circle */}
                <circle
                    stroke="currentColor"
                    fill="transparent"
                    strokeWidth={strokeWidth}
                    strokeDasharray={circumference + ' ' + circumference}
                    style={{ strokeDashoffset: 0 }}
                    r={normalizedRadius}
                    cx={radius}
                    cy={radius}
                    className="text-cyan-200 dark:text-cyan-800"
                />
                {/* Progress circle */}
                <circle
                    stroke="currentColor"
                    fill="transparent"
                    strokeWidth={strokeWidth}
                    strokeDasharray={circumference + ' ' + circumference}
                    style={{ strokeDashoffset }}
                    r={normalizedRadius}
                    cx={radius}
                    cy={radius}
                    className="text-cyan-500 transition-all duration-300 ease-in-out"
                    strokeLinecap="round"
                />
            </svg>
            {progress === 100 && (
                <div className="absolute inset-0 flex items-center justify-center">
                    <svg className="w-3.5 h-3.5 text-cyan-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                </div>
            )}
        </div>
    );
};

const EmbeddingProgress: React.FC<EmbeddingProgressProps> = ({className = "", isPreview, onDismiss}) => {
    const dynamicStateObj = useAppStore((state: any) => state.dynamicStateObj);
    const setDynamicStateObj = useAppStore((state: any) => state.setDynamicStateObj);
    const environmentObj = useAppStore((state: any) => state.environmentObj);
    const { environment, vscode } = environmentObj;

    const { embeddingInfo } = dynamicStateObj;
    const { status: embeddingStatus, progress } = embeddingInfo;

    // Don't render if:
    // - It's preview mode
    // - Status is neither active nor error
    // - Error is about no files needing updates (this is handled elsewhere)
    if (isPreview ||
        (!embeddingStatus || (embeddingStatus !== 'active' && embeddingStatus !== 'error')) ||
        (embeddingStatus === 'error' && embeddingInfo.error?.includes("No files found that need their embeddings updated"))) {
        return null;
    }

    const handleRetry = () => {
        setDynamicStateObj({ embeddingInfo: { status: null, error: null, progress: 0 } });
        requestProjectFiles(environment, vscode);
    };

    return (
        <div className={`mx-auto ${className}`}>
            <div className="bg-neutral-200/60 dark:bg-neutral-800/60 rounded-xl border border-neutral-300/30 dark:border-neutral-600/30 p-3 backdrop-blur-sm shadow-sm max-w-sm mx-auto relative">
                <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        {embeddingStatus === 'error' ? (
                            <div className="h-6 w-6 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-3.5 w-3.5 text-white"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </div>
                        ) : (
                            <ProgressCircle progress={progress} />
                        )}
                        <div className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                            {embeddingStatus === 'error' ? embeddingInfo.error : `Embedding in progress (${progress}%)`}
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        {embeddingStatus === 'error' && (
                            <button
                                onClick={handleRetry}
                                className="text-cyan-600 hover:text-cyan-500 dark:text-cyan-400 dark:hover:text-cyan-300 transition-colors text-sm font-medium"
                                aria-label="Retry embedding"
                            >
                                Retry
                            </button>
                        )}
                        <button
                            onClick={onDismiss}
                            className="text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 transition-colors"
                            aria-label="Dismiss embedding progress"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EmbeddingProgress;
